import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "../Pages/Navbar/Navbar";
import Slider from "react-slick";
import Swal from "sweetalert2";
import GulfWorkerLogo from "../Pages/Images/Gulf-Worker-Logo.webp";
import "./Home.css";
import "./Candidates/Candidates.css";
import "./DomesticHelper/DomesticHelper.css";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { FaYoutube, FaUserPlus, FaSearch, FaBriefcase, FaArrowRight } from "react-icons/fa";
import Footer from "../Footer/Footer"
import FeaturedProjects from "../FeaturedProjects/FeaturedProjects";
import GulfWorkerLeadBot from "../LeadBot/GulfWorkerLeadBot";
import BaseUrl from "../ApiServices/BaseUrl";
import AvailableCandidateCard from "../Common/AvailableCandidateCard";

const Homepage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [aboutSection, setAboutSection] = useState({ type: 'video', content: 'https://www.youtube.com/embed/DGMHCKzEOko?si=gxNIK5es7m0Iqykh' });
  const [isAboutLoaded, setIsAboutLoaded] = useState(false);
  const [availableJobsItems, setAvailableJobsItems] = useState([]);
  const [isAboutVideoActive, setIsAboutVideoActive] = useState(false);
  const [availableCandidates, setAvailableCandidates] = useState([]);

  const getYouTubeVideoId = (url) => {
    if (!url) return '';
    try {
      if (typeof url === 'string' && url.includes('<iframe')) {
        const srcMatch = url.match(/src="([^"]+)"/);
        if (srcMatch && srcMatch[1]) {
           url = srcMatch[1];
        }
      }
      const regExp = /^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=|shorts\/)([^#\&\?]*).*/;
      const match = url.match(regExp);
      if (match && match[2].length === 11) {
        return match[2];
      }
    } catch (e) {
      console.error("Error parsing YouTube URL:", e);
    }
    return null;
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
  };

  const getYouTubeThumbnailUrl = (url) => {
    const videoId = getYouTubeVideoId(url);
    return videoId ? `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg` : null;
  };

  useEffect(() => {
    fetch(`${BaseUrl}/api/about-section`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (data && data.type && data.content) {
          const normalizedType = String(data.type).toLowerCase();
          setAboutSection({
            type: normalizedType === 'image' ? 'image' : 'video',
            content: data.content
          });
          setIsAboutVideoActive(false);
        }
        setIsAboutLoaded(true);
      })
      .catch(() => {
        setIsAboutLoaded(true);
      });
  }, []);

  useEffect(() => {
    fetch(`${BaseUrl}/api/available-jobs`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        setAvailableJobsItems(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setAvailableJobsItems([]);
      });
  }, []);

  useEffect(() => {
    const fetchAvailableCandidates = async () => {
      try {
        const response = await fetch(
          `${BaseUrl}/api/job-seeker/combine/all?paginated=1&segment=domestic-staff&page=1&limit=15`
        );
        if (!response.ok) {
          setAvailableCandidates([]);
          return;
        }
        let payload = await response.json();
        let list = [];
        if (Array.isArray(payload)) {
          list = payload;
        } else if (payload?.data && Array.isArray(payload.data)) {
          list = payload.data;
        } else if (payload?.candidates && Array.isArray(payload.candidates)) {
          list = payload.candidates;
        } else if (payload?.users && Array.isArray(payload.users)) {
          list = payload.users;
        }

        const normalized = list
          .filter(Boolean)
          .map((candidate) => ({
            ...candidate,
            profile_photo: candidate.profile_photo
              ? `${BaseUrl}${candidate.profile_photo}`
              : candidate.profile_photo,
          }));

        setAvailableCandidates(normalized);
      } catch (err) {
        setAvailableCandidates([]);
      }
    };

    fetchAvailableCandidates();
  }, []);

  const handleCandidateViewClick = (userId) => {
    navigate(`/allcandidatesdetails/${userId}`);
  };

  const handleCandidateContactAction = (actionType, candidate = null) => {
    const user = JSON.parse(localStorage.getItem('user')) || {};
    if (!user.id) {
      Swal.fire({
        title: '<div class="swal-custom-title">Please Register or Login</div>',
        text: `You must be registered and logged in to contact this candidate via ${actionType}`,
        icon: 'info',
        showCancelButton: true,
        showDenyButton: true,
        confirmButtonText: 'Register',
        cancelButtonText: 'Login',
        denyButtonText: '<i class="bi bi-whatsapp"></i> Continue with WhatsApp',
        customClass: {
          popup: 'registration-popup',
          confirmButton: 'swal-register-btn',
          cancelButton: 'swal-login-btn',
          denyButton: 'swal-whatsapp-btn',
          actions: 'swal-actions-column'
        },
        buttonsStyling: false,
        showCloseButton: true,
        footer: '<div class="swal-footer-text">Registration helps prevent fake profiles</div>'
      }).then((result) => {
        if (result.isConfirmed) {
          navigate('/register', { state: { defaultTab: 'employer' } });
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          navigate('/login');
        } else if (result.isDenied) {
          const message = encodeURIComponent('Hello, I want to register on GulfWorker.\nPlease guide me with the registration process');
          window.open(`https://wa.me/97477651422?text=${message}`, '_blank');
        }
      });
      return;
    }

    if (actionType === 'whatsapp' && candidate?.phone_number) {
      window.open(`https://wa.me/${candidate.phone_number}`, '_blank');
    }
  };

  const normalizeRedirectRoute = (route) => {
    const value = (route || "").trim();
    if (!value) return "/alljobs";
    if (value.startsWith("http://") || value.startsWith("https://")) return value;
    return value.startsWith("/") ? value : `/${value}`;
  };

  const hasMoreThanFourAvailableJobs = availableJobsItems.length > 4;
  const aboutVideoThumbnail = getYouTubeThumbnailUrl(aboutSection.content);
  const aboutVideoEmbedUrl = getYouTubeEmbedUrl(aboutSection.content) || aboutSection.content;
  const availableJobsSettings = {
    dots: hasMoreThanFourAvailableJobs,
    infinite: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 2000,
    slidesToShow: Math.min(4, Math.max(1, availableJobsItems.length)),
    slidesToScroll: 1,
    arrows: hasMoreThanFourAvailableJobs,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: Math.min(4, Math.max(1, availableJobsItems.length)),
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(3, Math.max(1, availableJobsItems.length)),
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  const hasCandidatesToScroll = availableCandidates.length > 3;
  const availableCandidatesSettings = {
    dots: hasCandidatesToScroll,
    infinite: availableCandidates.length > 3,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 3500,
    pauseOnHover: true,
    slidesToShow: Math.min(3, Math.max(1, availableCandidates.length)),
    slidesToScroll: 1,
    arrows: hasCandidatesToScroll,
    responsive: [
      {
        breakpoint: 1400,
        settings: {
          slidesToShow: Math.min(3, Math.max(1, availableCandidates.length)),
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 1200,
        settings: {
          slidesToShow: Math.min(2, Math.max(1, availableCandidates.length)),
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 992,
        settings: {
          slidesToShow: Math.min(2, Math.max(1, availableCandidates.length)),
          slidesToScroll: 1,
        }
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        }
      }
    ]
  };

  return (
    <>
      <Navbar />
      <div className="homepage-body">

        <div className="homepage-hero">
          <video
            className="homepage-hero-video"
            autoPlay
            loop
            muted
            playsInline
          >
            <source src="/gulftop.mp4" type="video/mp4" />
            <source src="/gulf_top.mp4" type="video/mp4" />
            Your browser does not support the video tag.
          </video>
          <div className="homepage-overlay">
            {/* <div className="homepage-hero-header">
              <h1 className="homepage-title">{t("hero.title")}</h1>
              <p className="homepage-subtitle">{t("hero.subtitle")}</p>
            </div> */}
            <div className="homepage-buttons-container">
              <div className="homepage-buttons-row">
                {/* Register Now Card */}
                <a href="/register" className="glass-card-btn">
                  <div className="glass-card-icon-wrapper icon-bg-green">
                    <FaUserPlus className="glass-card-icon" />
                  </div>
                  <div className="glass-card-content">
                    <h3 className="glass-card-title">{t("hero.register_now", "Register Now - Free")}</h3>
                    <p className="glass-card-subtitle">{t("hero.register_now_sub", "Create your account in minutes")}</p>
                  </div>
                  <FaArrowRight className="glass-card-arrow" />
                </a>

                {/* How to Register Card */}
                <a 
                  href="https://youtube.com/shorts/8H4sfSj5k1Y?si=gv-0ee-7lN7ohUKg" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="glass-card-btn"
                >
                  <div className="glass-card-icon-wrapper icon-bg-red">
                    <FaYoutube className="glass-card-icon" />
                  </div>
                  <div className="glass-card-content">
                    <h3 className="glass-card-title">{t("hero.how_to_register", "How to Register")}</h3>
                    <p className="glass-card-subtitle">{t("hero.how_to_register_sub", "Watch step-by-step guide")}</p>
                  </div>
                  <FaArrowRight className="glass-card-arrow" />
                </a>
              </div>

              <div className="homepage-buttons-row">
                {/* Find Candidates Card */}
                <a href="/allcandidates" className="glass-card-btn">
                  <div className="glass-card-icon-wrapper icon-bg-yellow">
                    <FaSearch className="glass-card-icon" />
                  </div>
                  <div className="glass-card-content">
                    <h3 className="glass-card-title">{t("hero.find_candidates", "Find Candidates")}</h3>
                    <p className="glass-card-subtitle">{t("hero.find_candidates_sub", "Browse verified driver profiles")}</p>
                  </div>
                  <FaArrowRight className="glass-card-arrow" />
                </a>

                {/* Find Jobs Card */}
                <a href="/alljobs" className="glass-card-btn">
                  <div className="glass-card-icon-wrapper icon-bg-emerald">
                    <FaBriefcase className="glass-card-icon" />
                  </div>
                  <div className="glass-card-content">
                    <h3 className="glass-card-title">{t("hero.find_jobs", "Find Jobs")}</h3>
                    <p className="glass-card-subtitle">{t("hero.find_jobs_sub", "Explore latest job openings")}</p>
                  </div>
                  <FaArrowRight className="glass-card-arrow" />
                </a>
              </div>
            </div>
          </div>
        </div>

        <section className="phone-section">
          <div className="phone-right">
            <div className="phone-community-row">
              <span className="phone-community-arrow">➤</span>
              <span className="phone-community-text">{t("about.community")}</span>
            </div>
            <h2 className="phone-title">
              {t("about.title")}
            </h2>
            <p className="phone-description">
              {t("about.description_part1")}
              <br /><br />
              {t("about.description_part2")}
            </p>
            <div className="phone-buttons">
              <a href="/alljobs" className="phone-btn phone-btn-job">{t("about.find_jobs")}</a>
              <a href="/allcandidates" className="phone-btn phone-btn-candidate">{t("about.hire_talent")}</a>
            </div>
          </div>
          <div className="phone-left">
            {aboutSection.type === 'video' ? (
              isAboutVideoActive ? (
                <iframe
                  className="phone-image mx-auto"
                  src={aboutVideoEmbedUrl}
                  title="YouTube video player"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  loading="lazy"
                  style={{ borderRadius: '8px', border: 'none', width: '100%', minHeight: '300px' }}
                ></iframe>
              ) : (
                <button
                  type="button"
                  className="phone-video-preview"
                  onClick={() => setIsAboutVideoActive(true)}
                  aria-label={t("about.play_video")}
                >
                  {aboutVideoThumbnail ? (
                    <img
                      src={aboutVideoThumbnail}
                      alt={t("about.play_video")}
                      className="phone-image"
                      loading="lazy"
                      decoding="async"
                      style={{ objectFit: 'cover', backgroundColor: '#000' }}
                    />
                  ) : (
                    <div className="phone-image phone-video-fallback">{t("about.play_video")}</div>
                  )}
                  <span className="phone-video-play-button" aria-hidden="true">
                    <FaYoutube className="youtube-icon-red" />
                  </span>
                </button>
              )
            ) : (
              <img
                src={aboutSection.content}
                alt={t("navbar.about_us")}
                className="phone-image"
                style={{ borderRadius: '8px', objectFit: 'contain', objectPosition: 'center', backgroundColor: 'transparent' }}
              />
            )}
            {!isAboutLoaded && (
              <div className="text-muted small mt-2">{t("about.loading_media")}</div>
            )}
          </div>
        </section>

        <section className="easily-find">
          <div className="section-container">
            <div className="section-header">
              <span className="section-arrow">➤</span>
              <span className="section-text section-text-single">
                {t("jobs_section.title")}
              </span>
            </div>
            {availableJobsItems.length > 0 ? (
              <Slider {...availableJobsSettings} className="helper-carousel six-cards-slider">
                {availableJobsItems.map((item) => {
                  const href = normalizeRedirectRoute(item.redirect_route);
                  const isExternal = href.startsWith("http://") || href.startsWith("https://");
                  return (
                    <div key={item.id} className="available-job-slide">
                      <a
                        href={href}
                        className="available-job-banner-card"
                        target={isExternal ? "_blank" : "_self"}
                        rel={isExternal ? "noopener noreferrer" : undefined}
                      >
                        <img
                          src={item.image_url}
                          alt="Available Job Opportunity"
                          className="available-job-banner-image"
                          loading="lazy"
                          decoding="async"
                        />
                      </a>
                    </div>
                  );
                })}
              </Slider>
            ) : (
              <div className="text-muted text-center py-3">
                {t("jobs_section.no_banners")}
              </div>
            )}
            <div className="section-btn-container">
              <a href="/alljobs" className="section-btn section-btn-job">
                {t("jobs_section.search_job")}
              </a>
            </div>
          </div>
        </section>

        <section className="available-jobs available-candidates-section">
          <div className="section-container">
            <div className="section-header">
              <span className="section-arrow">➤</span>
              <span className="section-text section-text-single">
                {t("candidates_section.title")}
              </span>
            </div>
            {availableCandidates.length > 0 ? (
              <Slider {...availableCandidatesSettings} className="available-candidates-carousel">
                {availableCandidates.map((candidate) => (
                  <div key={candidate.id || candidate.user_id} className="available-candidate-slide">
                    <AvailableCandidateCard
                      candidate={candidate}
                      onViewClick={() => handleCandidateViewClick(candidate.user_id || candidate.id)}
                      onContactAction={handleCandidateContactAction}
                    />
                  </div>
                ))}
              </Slider>
            ) : (
              <div className="text-muted text-center py-4">
                {t("candidates_section.no_candidates")}
              </div>
            )}
            <div className="section-btn-container">
              <a href="/allcandidates" className="section-btn section-btn-job">
                {t("candidates_section.view_all")}
              </a>
            </div>
          </div>
        </section>

      </div>
      <FeaturedProjects />
      <Footer />
      <GulfWorkerLeadBot />
    </>
  );
};

export default Homepage;
